import * as CL from '@design-system/component-library';
import { useState } from 'react';
import { dsClass } from '../../common/constants/dsClasses.js';
import { downloadOrderConfirmationPdf } from '../../common/fetch.js';
import {
  contractEndDateMsg,
  downloadOrderConfirmationMsg,
  durationOfContractMsg,
  installationAddressMsg,
  orderConfirmationNotAvailableMsg,
  simCardNumberMsg,
  subscriptionNumberMsg,
  t,
  technicalErrorMsg,
} from '../../common/i18n/index.js';
import { formatTimestampToUTCDDMMYYYY } from '../../common/utils/dateUtils.js';
import { exportDataFromURI } from '../../common/utils/domUtils.js';
import { getTaapiTechnologyAsString } from '../../common/utils/subscriptionUtils.js';
import { AuthenticatedUserRole, DeliveryOrder } from '../../generated/api/models.js';
import { useAuth } from '../../public/site/AuthProvider.js';
import type { DeliveryOrderDetails } from '../../generated/api/models.js';

interface DeliveryOrderDetailsProductProps {
  productType: DeliveryOrder.ProductTypeEnum;
  details: DeliveryOrderDetails;
  deliveryOrderName?: string;
  customerOrderDisplayId?: string;
  showOrderConfirmationDownload?: boolean;
}

export const DeliveryOrderDetailsProduct = ({
  productType,
  details,
  deliveryOrderName,
  customerOrderDisplayId,
  showOrderConfirmationDownload = true,
}: DeliveryOrderDetailsProductProps) => {
  const { contractTermMonths, fixedTermContractEndDate, taapiTechnology } = details;
  const { authenticatedUser } = useAuth();
  const isEmployee =
    authenticatedUser?.userRole === AuthenticatedUserRole.EMPLOYEE ||
    authenticatedUser?.userRole === AuthenticatedUserRole.CUSTOMER_EMPLOYEE;
  const [downloadError, setDownloadError] = useState<'notFound' | 'error' | false>(false);

  const downloadOrderConfirmation = async () => {
    setDownloadError(false);
    const response = await downloadOrderConfirmationPdf(customerOrderDisplayId!);
    if (response.ok) {
      const blob = await response.blob();
      const header = response.headers.get('Content-Disposition');
      const fileName = header
        ? header.split('=')[1].replace(/['"]+/g, '')
        : `order-confirmation-${customerOrderDisplayId}.pdf`;
      exportDataFromURI(blob, fileName);
    } else {
      setDownloadError(response.status === 404 ? 'notFound' : 'error');
    }
  };

  return (
    <div className="of-delivery-order-details--section" key="of-delivery-order--section-product">
      <h3 className={dsClass.H3}>{t.B4ZV('Product details')}</h3>
      <div className={`of-delivery-order-details--section-product ${dsClass.DISPLAY_FLEX}`}>
        <CL.Description
          items={[
            {
              title: t.XSQ0('Product name'),
              description: deliveryOrderName,
            },
            {
              title: t.CAA9(durationOfContractMsg),
              description: contractTermMonths ? `${contractTermMonths} ${t.XXVX('month')}` : undefined,
            },
            {
              title: t.LHJ4(contractEndDateMsg),
              description: fixedTermContractEndDate
                ? formatTimestampToUTCDDMMYYYY(fixedTermContractEndDate)
                : undefined,
            },
            {
              title: t.HOPN('Administrator’s email address'),
              description:
                productType === DeliveryOrder.ProductTypeEnum.SOFTWARE_PRODUCT ? details.adminEmailAddress : undefined,
            },
            {
              title: t.R4O4('Downloading speed'),
              description: details.downloadSpeed,
            },
            {
              title: t.DI4V(installationAddressMsg),
              description: details.installationAddress,
            },
            {
              title: t.DP44(subscriptionNumberMsg),
              description: details.mobileNumber,
            },
            {
              title: t.FDQN('IMEI code or serial number'),
              description: details.serialNumber,
            },
            {
              title: t.VLQ3(simCardNumberMsg),
              description: details.simCardNumber,
            },
            {
              title: t.BBPX('Software version'),
              description: details.softwareEditionName,
            },
            {
              title: t.FWV1('Broadband technology'),
              description: getTaapiTechnologyAsString(taapiTechnology),
            },
            {
              title: t.CQQ7('Transmission speed'),
              description: details.uploadSpeed,
            },
            {
              title: t.ECMJ('Device model'),
              description: details.deviceModel,
            },
          ].filter(item => !!item && item.description)}
        />
        {customerOrderDisplayId && (showOrderConfirmationDownload || !isEmployee) && (
          <div>
            <button
              className={`${dsClass.BUTTON} ${dsClass.BUTTON_COLOR_LIGHT} ${dsClass.BUTTON_SIZE_M} ${dsClass.DISPLAY_FLEX} ${dsClass.ALIGN_ITEMS_CENTER}`}
              onClick={downloadOrderConfirmation}
            >
              <CL.Icon className={`${dsClass.MARGIN_RIGHT_2}`} icon="download" type="filled" />
              {t.FHKH(downloadOrderConfirmationMsg)}
            </button>
            {downloadError && (
              <CL.Notification
                className={`${dsClass.MARGIN_TOP_2} ${dsClass.MARGIN_BOTTOM_0}`}
                type="error"
                text={
                  downloadError === 'notFound' ? t.UJGD(orderConfirmationNotAvailableMsg) : t.T4OU(technicalErrorMsg)
                }
                visible={true}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};
